import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import { AnimatedBackground } from '../../containers/Results/View/ResultsList/Loading';
import { formatRoundPrice, getRoundPrice } from '../../helpers/results';
import { SearchIcon } from '../../images/icons/search';
import { PriceCalendarTrip } from '../../services/priceCalendar/types';
import { colors } from '../../styles/constants';
import { usePriceCalendarBody } from './body.hook';
import { CellStyle } from './common';
import messages from './messages';
import { PriceCalendarBodyProps, PriceCalendarDayProps } from './types';
import { getCalendarDays } from './utils';

const PriceCalendarBody = (props: PriceCalendarBodyProps) => {
  const { destination, origin, setCheapestDate } = props;
  const { calendarDays, end, start } = useMemo(() => getCalendarDays(), []);
  const { getDayProps } = usePriceCalendarBody({
    start,
    end,
    origin,
    destination,
    setCheapestDate,
  });

  return (
    <tbody>
      {calendarDays.map((week, weekIndex) => (
        <tr key={weekIndex}>
          {week.map((day, dayIndex) => {
            const dayProps = getDayProps({
              day,
              weekIndex,
              dayIndex,
            });

            return <PriceCalendarDay {...dayProps} key={dayIndex} />;
          })}
        </tr>
      ))}
    </tbody>
  );
};

const PriceCalendarDayContainer = styled.a<{ isInPast: boolean }>`
  ${CellStyle}
  text-decoration: none;
  ${tw`bg-white rounded-15 border border-solid border-grey-200 flex flex-col items-center justify-start text-text`}
  ${({ isInPast }) => isInPast && tw`bg-grey-100 cursor-not-allowed`}
  ${({ isInPast }) =>
    isInPast === false && tw`cursor-pointer hover:border-grey-300`}
`;

const CalendarDay = styled.span`
  ${tw`text-p-medium-m leading-p-medium-m`}
`;

const PriceCalendarDay = (props: PriceCalendarDayProps) => {
  const { day, isCheapest, isInPast, isLoading, isToday, searchLink, trip } =
    props;

  return (
    <td>
      <PriceCalendarDayContainer
        as={isInPast ? 'span' : 'a'}
        href={searchLink}
        isInPast={isInPast}
      >
        <CalendarDay>
          {isToday ? <FormattedMessage {...messages.today} /> : day}
        </CalendarDay>
        <CalendarPrice
          isCheapest={isCheapest}
          isInPast={isInPast}
          isLoading={isLoading}
          trip={trip}
        />
      </PriceCalendarDayContainer>
    </td>
  );
};

const LoadingPrice = styled(AnimatedBackground)`
  ${tw`w-full rounded-15`}
  height: 15px;
`;

const CheapestPriceTag = styled.span`
  ${tw`bg-accent-yellow-400 rounded-30 p-0 px-small text-p-xsmall-m leading-p-xsmall-m font-bold`}
  white-space: nowrap
`;

const CalendarPriceContainer = styled.span`
  ${tw`text-title-xlarge-m leading-title-xlarge-m font-kyrial`}
`;

type CalendarPriceProps = {
  isInPast: boolean;
  isLoading: boolean;
  isCheapest: boolean;
  trip: PriceCalendarTrip;
};

const CalendarPrice = (props: CalendarPriceProps) => {
  const { isCheapest, isInPast, isLoading, trip } = props;

  if (isInPast) {
    return null;
  }

  if (isLoading === true) {
    return <LoadingPrice />;
  }

  if (trip === null) {
    return (
      <span>
        <SearchIcon color={colors.primaryLight100} />
      </span>
    );
  }

  return (
    <React.Fragment>
      <CalendarPriceContainer>
        {formatRoundPrice(getRoundPrice(trip.priceCents))}
      </CalendarPriceContainer>
      {isCheapest && (
        <CheapestPriceTag>
          <FormattedMessage {...messages.cheapest} />
        </CheapestPriceTag>
      )}
    </React.Fragment>
  );
};

export default PriceCalendarBody;
